import { Text } from '@radix-ui/themes';
import { CommonFormGrid } from 'components/form/grid';
import { FormHeading } from 'components/form/heading';
import { FormSelect } from 'components/form/select';
import { FormTextField } from 'components/form/textfield';
import { EB } from 'components/layout/error-boundary';
import { FormContext } from 'contexts/form';
import { endOfMonth, isValid } from 'date-fns';
import { OptionDef } from 'interfaces/options';
import { Currency } from 'lib/enums/currency';
import { DividendType, TransactionType } from 'lib/enums/transaction';
import { TransactionDoc } from 'lib/interfaces/database/transaction';
import { StringUtil } from 'lib/utilities/string';
import { useContext, useEffect, useMemo, useState } from 'react';
import useAccountsStore from 'stores/accounts';
import useDealersStore from 'stores/dealers';
import useInvestorsStore from 'stores/investors';
import useSecuritiesStore from 'stores/securities';

export const TransactionForm = () => {
  const { data: investors, load: loadInvestors } = useInvestorsStore();
  const { data: accounts, load: loadAccounts } = useAccountsStore();
  const { data: securities, load: loadSecurities } = useSecuritiesStore();
  const { data: dealers, load: loadDealers } = useDealersStore();

  useEffect(() => {
    loadInvestors();
    loadAccounts();
    loadSecurities();
    loadDealers();
  }, []);

  const { data, setData } = useContext(FormContext);

  const [defaultData] = useState<Partial<TransactionDoc>>(data);

  const typedData: Partial<TransactionDoc> = data;

  const issuerOptions = useMemo<OptionDef[]>(
    () =>
      investors.map((m) => ({
        label: m.preferredName,
        // don't use id because that's the id of the InvestorPrivates record
        value: m.investorID,
        group: m.type,
      })),
    [investors]
  );

  const accountOptions = useMemo<OptionDef[]>(() => {
    return accounts
      .filter((m) => m.investorID === typedData.investorID)
      .map((m) => ({
        label: m.shortIdentifier,
        value: m.id,
        group: m.type,
      }));
  }, [accounts, investors, typedData]);

  const securityOptions = useMemo<OptionDef[]>(
    () =>
      securities.map((m) => ({
        label: m.name,
        value: m.id,
        group: `${m.class}: ${m.type}`,
      })),
    [securities]
  );

  const dealerOptions = useMemo<OptionDef[]>(() => {
    const output: OptionDef[] = [];

    for (const m of dealers) {
      if (m.dealerID) {
        output.push({
          label: m.fullName,
          value: m.dealerID,
        });
      }
    }

    return output;
  }, [dealers]);

  return (
    <EB>
      <CommonFormGrid columns="2">
        <FormHeading label="Transaction" gridColumn="span 2" />

        <FormSelect
          label="Investor"
          defaultValue={defaultData.investorID}
          options={issuerOptions}
          onChange={(v) => {
            const payload: Partial<TransactionDoc> = {
              investorID: v,
            };
            setData(payload);
          }}
          required
        />

        <FormSelect
          label="Account"
          defaultValue={defaultData.accountID}
          options={accountOptions}
          onChange={(v) => {
            const payload: Partial<TransactionDoc> = {
              accountID: v,
            };
            setData(payload);
          }}
          required
        />

        <FormSelect
          label="Security"
          defaultValue={defaultData.securityID}
          options={securityOptions}
          onChange={(v) => {
            const payload: Partial<TransactionDoc> = {
              securityID: v,
            };
            setData(payload);
          }}
          required
        />

        <FormSelect
          label="Dealer"
          defaultValue={defaultData.dealerID}
          options={dealerOptions}
          onChange={(v) => {
            const payload: Partial<TransactionDoc> = {
              dealerID: v,
            };
            setData(payload);
          }}
          required
        />

        <FormTextField
          label="Transaction Date"
          defaultValue={StringUtil.printDate(defaultData.transDate)}
          onDateChange={(v, vd) => {
            const payload: Partial<TransactionDoc> = {
              transDate: v,
              monthEndDate: endOfMonth(vd).toISOString(),
            };
            setData(payload);
          }}
          hint={
            typedData.monthEndDate && isValid(typedData.monthEndDate)
              ? `Month End: ${StringUtil.printDate(typedData.monthEndDate)}`
              : undefined
          }
          required
        />

        <FormTextField
          label="Settlement Date"
          defaultValue={StringUtil.printDate(defaultData.settleDate)}
          onOptionalDateChange={(v) => {
            const payload: Partial<TransactionDoc> = {
              settleDate: v,
            };
            setData(payload);
          }}
        />

        <FormSelect
          label="Type"
          defaultValue={defaultData.type}
          options={Object.values(TransactionType).map((o) => ({
            label: o,
            value: o,
          }))}
          onChange={(v) => {
            const payload: Partial<TransactionDoc> = {
              type: v as TransactionType,
            };
            setData(payload);
          }}
          required
        />

        <FormSelect
          label="Dividend Type"
          defaultValue={defaultData.dividendType}
          options={Object.values(DividendType).map((o) => ({
            label: o,
            value: o,
          }))}
          onOptionalChange={(v) => {
            const payload: Partial<TransactionDoc> = {
              dividendType: v as DividendType,
            };
            setData(payload);
          }}
        />

        <FormTextField
          label="Amount"
          type="number"
          defaultValue={defaultData.amount?.toFixed(2)}
          onNumericChange={(v) => {
            const payload: Partial<TransactionDoc> = {
              amount: v,
            };
            setData(payload);
          }}
          required
        />

        <FormSelect
          label="Currency"
          defaultValue={defaultData.currency}
          options={Object.values(Currency).map((c) => ({
            label: c,
            value: c,
          }))}
          onChange={(v) => {
            const payload: Partial<TransactionDoc> = {
              currency: v as Currency,
            };
            setData(payload);
          }}
          required
        />

        <FormTextField
          label="Units of Security"
          type="number"
          defaultValue={defaultData.units?.toFixed(2)}
          onOptionalNumericChange={(v) => {
            const payload: Partial<TransactionDoc> = {
              units: v,
            };
            setData(payload);
          }}
        />

        <FormSelect
          label="Auto-Pricing"
          defaultValue={defaultData.autoPricing?.toString()}
          tooltip={
            <Text>
              Enable Auto-Pricing for the server to detect and create any
              necessary Price record based on this security's amount, settlement
              date, and units transacted whenever this transaction is created
              and modified.
            </Text>
          }
          options={[
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' },
          ]}
          onOptionalBooleanChange={(v) => {
            const payload: Partial<TransactionDoc> = {
              autoPricing: v,
            };
            setData(payload);
          }}
        />

        <FormTextField
          gridColumnStart="1"
          label="Confirmation Date"
          defaultValue={StringUtil.printDate(defaultData.confirmDate)}
          onOptionalDateChange={(v) => {
            const payload: Partial<TransactionDoc> = {
              confirmDate: v,
            };
            setData(payload);
          }}
        />

        <FormSelect
          label="Confirmation Sent"
          defaultValue={defaultData.confirmSent?.toString()}
          options={[
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' },
          ]}
          onOptionalBooleanChange={(v) => {
            const payload: Partial<TransactionDoc> = {
              confirmSent: v,
            };
            setData(payload);
          }}
        />
      </CommonFormGrid>
    </EB>
  );
};
